import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { projectApi } from '@/lib/api'
import { useToast } from '@/stores/toast-store'

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
}

export default function NewProjectPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [name, setName] = useState('')
  const [projectSlug, setProjectSlug] = useState('')
  const [description, setDescription] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleNameChange(value: string) {
    setName(value)
    if (!slugEdited) {
      setProjectSlug(toSlug(value))
    }
  }

  function handleSlugChange(value: string) {
    setProjectSlug(toSlug(value))
    setSlugEdited(true)
  }

  const createMutation = useMutation({
    mutationFn: () =>
      projectApi.createProject(slug!, {
        name: name.trim(),
        slug: projectSlug,
        description: description.trim() || undefined,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['workspace', slug, 'projects'] })
      toast({ title: 'Project berhasil dibuat' })
      navigate(`/workspaces/${slug}/projects/${created.id}`, { replace: true })
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Gagal membuat project.')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Nama project wajib diisi.')
      return
    }
    if (!projectSlug) {
      setError('Slug project wajib diisi.')
      return
    }
    createMutation.mutate()
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Buat Project</h1>
        <p className="text-sm text-muted-foreground">Tambahkan project baru ke workspace ini.</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-lg rounded-xl border border-border bg-card p-6 space-y-5">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="text-sm font-medium">
            Nama <span className="text-destructive">*</span>
          </label>
          <input
            id="name"
            type="text"
            required
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Nama project"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Slug */}
        <div className="space-y-1.5">
          <label htmlFor="slug" className="text-sm font-medium">
            Slug <span className="text-destructive">*</span>
          </label>
          <input
            id="slug"
            type="text"
            required
            value={projectSlug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="nama-project"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            Identifier unik project dalam workspace ini.
          </p>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label htmlFor="description" className="text-sm font-medium">
            Deskripsi
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Deskripsi project (opsional)"
            rows={3}
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {createMutation.isPending ? 'Menyimpan...' : 'Buat Project'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/workspaces/${slug}/projects`)}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  )
}
